import * as React from "react"
import {
  UploadCloud,
  FileArchive,
  CheckCircle2,
  AlertCircle,
  Loader2,
  Sparkles,
  ArrowRight,
  RotateCcw,
  Check,
} from "lucide-react"
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from "@/shared/ui/atoms/Dialog"
import { Button } from "@/shared/ui/atoms/Button"
import { Switch } from "@/shared/ui/atoms/Switch"
import { Label } from "@/shared/ui/atoms/Label"
import { Badge } from "@/shared/ui/atoms/Badge"
import { Alert, AlertDescription, AlertTitle } from "@/shared/ui/atoms/Alert"
import {
  importNotionZip,
  type NotionImportResponse,
  type NotionImportSummary,
} from "@/features/transactions/api/importApi"
import { useQueryClient } from "@tanstack/react-query"
import { toast } from "sonner"

interface NotionImportModalProps {
  open: boolean
  onOpenChange: (open: boolean) => void
  onSuccess?: (summary: NotionImportSummary) => void
}

const MAX_FILE_SIZE = 25 * 1024 * 1024 // 25 MB

export function NotionImportModal({
  open,
  onOpenChange,
  onSuccess,
}: NotionImportModalProps) {
  const [file, setFile] = React.useState<File | null>(null)
  const [dryRun, setDryRun] = React.useState<boolean>(true)
  const [isLoading, setIsLoading] = React.useState<boolean>(false)
  const [error, setError] = React.useState<string | null>(null)
  const [result, setResult] = React.useState<NotionImportResponse | null>(null)
  const [isDragging, setIsDragging] = React.useState<boolean>(false)

  const queryClient = useQueryClient()
  const fileInputRef = React.useRef<HTMLInputElement | null>(null)

  const resetState = () => {
    setFile(null)
    setDryRun(true)
    setIsLoading(false)
    setError(null)
    setResult(null)
    setIsDragging(false)
  }

  const handleOpenChange = (isOpen: boolean) => {
    if (!isLoading) {
      if (!isOpen) resetState()
      onOpenChange(isOpen)
    }
  }

  const validateAndSetFile = (selectedFile: File) => {
    setError(null)
    if (!selectedFile.name.toLowerCase().endsWith(".zip")) {
      setError("El archivo seleccionado debe ser un archivo comprimido .zip exportado de Notion.")
      return
    }
    if (selectedFile.size > MAX_FILE_SIZE) {
      setError("El archivo supera el tamaño máximo permitido de 25 MB.")
      return
    }
    setFile(selectedFile)
  }

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files && e.target.files.length > 0) {
      validateAndSetFile(e.target.files[0])
    }
  }

  const handleDragOver = (e: React.DragEvent) => {
    e.preventDefault()
    setIsDragging(true)
  }

  const handleDragLeave = () => {
    setIsDragging(false)
  }

  const handleDrop = (e: React.DragEvent) => {
    e.preventDefault()
    setIsDragging(false)
    if (e.dataTransfer.files && e.dataTransfer.files.length > 0) {
      validateAndSetFile(e.dataTransfer.files[0])
    }
  }

  const handleExecuteImport = async (targetDryRun: boolean) => {
    if (!file) {
      setError("Por favor selecciona un archivo .zip para continuar.")
      return
    }

    setIsLoading(true)
    setError(null)

    try {
      const response = await importNotionZip(file, targetDryRun)
      setResult(response)

      if (!targetDryRun) {
        // Invalidate queries so dashboard, accounts, and transactions immediately refresh
        queryClient.invalidateQueries({ queryKey: ["dashboard-summary"] })
        queryClient.invalidateQueries({ queryKey: ["accounts"] })
        queryClient.invalidateQueries({ queryKey: ["expenses"] })
        queryClient.invalidateQueries({ queryKey: ["incomes"] })
        queryClient.invalidateQueries({ queryKey: ["savings"] })
        queryClient.invalidateQueries({ queryKey: ["transactions"] })

        toast.success("¡Importación completada con éxito!", {
          description: `Se importaron ${response.summary.incomes_imported} ingresos, ${response.summary.expenses_imported} gastos y ${response.summary.savings_imported} ahorros.`,
        })

        if (onSuccess) {
          onSuccess(response.summary)
        }
      }
    } catch (err: unknown) {
      let backendMessage = "Ocurrió un error inesperado al procesar el archivo de Notion."
      if (err && typeof err === "object" && "response" in err) {
        const responseData = (err as { response?: { data?: { message?: string } } }).response?.data
        if (responseData?.message) {
          backendMessage = responseData.message
        }
      } else if (err instanceof Error) {
        backendMessage = err.message
      }
      setError(backendMessage)
    } finally {
      setIsLoading(false)
    }
  }

  return (
    <Dialog open={open} onOpenChange={handleOpenChange}>
      <DialogContent className="sm:max-w-xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <div className="flex items-center gap-2">
            <div className="flex size-9 items-center justify-center rounded-lg bg-primary/10 text-primary">
              <Sparkles className="size-5" />
            </div>
            <div>
              <DialogTitle className="text-xl font-bold">
                Importar Datos desde Notion
              </DialogTitle>
              <DialogDescription className="text-xs sm:text-sm text-muted-foreground mt-0.5">
                Transfiere tu historial financiero (cuentas, ingresos, gastos y ahorros) a Cartera.
              </DialogDescription>
            </div>
          </div>
        </DialogHeader>

        {/* Error Alert */}
        {error && (
          <Alert variant="destructive" className="my-2">
            <AlertCircle className="size-4" />
            <AlertTitle>Error en la importación</AlertTitle>
            <AlertDescription className="text-xs">{error}</AlertDescription>
          </Alert>
        )}

        {/* Main Flow: Upload or Results */}
        {!result ? (
          <div className="space-y-5 py-2">
            {/* Drag and Drop Zone */}
            <div
              onDragOver={handleDragOver}
              onDragLeave={handleDragLeave}
              onDrop={handleDrop}
              onClick={() => fileInputRef.current?.click()}
              className={`relative flex flex-col items-center justify-center rounded-xl border-2 border-dashed p-6 text-center transition-all cursor-pointer ${
                isDragging
                  ? "border-primary bg-primary/5 scale-[0.99]"
                  : file
                  ? "border-emerald-500/50 bg-emerald-50/30 dark:bg-emerald-950/10"
                  : "border-border hover:border-primary/50 hover:bg-muted/50"
              }`}
            >
              <input
                ref={fileInputRef}
                type="file"
                accept=".zip"
                className="hidden"
                onChange={handleFileChange}
                disabled={isLoading}
              />

              {file ? (
                <div className="flex flex-col items-center gap-2">
                  <div className="flex size-12 items-center justify-center rounded-full bg-emerald-500/10 text-emerald-600 dark:text-emerald-400">
                    <FileArchive className="size-6" />
                  </div>
                  <div className="space-y-0.5">
                    <p className="text-sm font-semibold text-foreground break-all">
                      {file.name}
                    </p>
                    <p className="text-xs text-muted-foreground">
                      {(file.size / (1024 * 1024)).toFixed(2)} MB • Listo para procesar
                    </p>
                  </div>
                  <Badge variant="secondary" className="mt-1 text-xs">
                    Haz clic para cambiar de archivo
                  </Badge>
                </div>
              ) : (
                <div className="flex flex-col items-center gap-2.5">
                  <div className="flex size-12 items-center justify-center rounded-full bg-muted text-muted-foreground">
                    <UploadCloud className="size-6" />
                  </div>
                  <div className="space-y-1">
                    <p className="text-sm font-medium text-foreground">
                      Arrastra tu archivo <span className="font-semibold text-primary">.zip</span> de Notion aquí
                    </p>
                    <p className="text-xs text-muted-foreground">
                      o haz clic para explorar en tu equipo (Máximo 25 MB)
                    </p>
                  </div>
                </div>
              )}
            </div>

            {/* Mode Switch (Dry Run vs Live) */}
            <div className="flex items-center justify-between rounded-lg border border-border p-3.5 bg-muted/30">
              <div className="space-y-0.5">
                <div className="flex items-center gap-2">
                  <Label htmlFor="dry-run-switch" className="text-sm font-semibold cursor-pointer">
                    Modo Simulación (Dry-Run)
                  </Label>
                  <Badge variant={dryRun ? "default" : "secondary"} className="text-[10px] uppercase font-bold">
                    {dryRun ? "Recomendado" : "Live"}
                  </Badge>
                </div>
                <p className="text-xs text-muted-foreground">
                  {dryRun
                    ? "Valida el archivo y calcula los balances sin guardar cambios en la base de datos."
                    : "Guarda permanentemente las transacciones y actualiza tus cuentas."}
                </p>
              </div>
              <Switch
                id="dry-run-switch"
                checked={dryRun}
                onCheckedChange={setDryRun}
                disabled={isLoading}
              />
            </div>
          </div>
        ) : (
          /* Results View */
          <div className="space-y-4 py-2">
            <div className="flex items-center justify-between rounded-lg bg-emerald-500/10 border border-emerald-500/20 p-3.5 text-emerald-900 dark:text-emerald-100">
              <div className="flex items-center gap-2.5">
                <CheckCircle2 className="size-5 text-emerald-600 dark:text-emerald-400 shrink-0" />
                <div>
                  <h4 className="text-sm font-bold">
                    {result.dry_run ? "Simulación Exitosa (Dry-Run)" : "¡Importación Completada!"}
                  </h4>
                  <p className="text-xs text-muted-foreground">
                    Procesado en {result.execution_time_ms} ms sin inconsistencias.
                  </p>
                </div>
              </div>
              <Badge variant={result.dry_run ? "outline" : "default"}>
                {result.dry_run ? "Sin cambios guardados" : "Guardado en BD"}
              </Badge>
            </div>

            {/* Metrics Breakdown Grid */}
            <div className="grid grid-cols-3 gap-2.5 text-center">
              <div className="rounded-lg border border-border bg-card p-2.5">
                <p className="text-[11px] font-medium text-muted-foreground uppercase tracking-wider">
                  Ingresos
                </p>
                <p className="text-lg font-bold text-foreground mt-0.5">
                  {result.summary.incomes_imported}
                </p>
                <p className="text-[10px] text-emerald-600 dark:text-emerald-400 font-semibold">
                  L. {Number(result.summary.total_income_amount).toLocaleString("es-HN", { minimumFractionDigits: 2 })}
                </p>
              </div>

              <div className="rounded-lg border border-border bg-card p-2.5">
                <p className="text-[11px] font-medium text-muted-foreground uppercase tracking-wider">
                  Gastos
                </p>
                <p className="text-lg font-bold text-foreground mt-0.5">
                  {result.summary.expenses_imported}
                </p>
                <p className="text-[10px] text-rose-600 dark:text-rose-400 font-semibold">
                  L. {Number(result.summary.total_expense_amount).toLocaleString("es-HN", { minimumFractionDigits: 2 })}
                </p>
              </div>

              <div className="rounded-lg border border-border bg-card p-2.5">
                <p className="text-[11px] font-medium text-muted-foreground uppercase tracking-wider">
                  Ahorros
                </p>
                <p className="text-lg font-bold text-foreground mt-0.5">
                  {result.summary.savings_imported}
                </p>
                <p className="text-[10px] text-blue-600 dark:text-blue-400 font-semibold">
                  L. {Number(result.summary.total_saving_amount).toLocaleString("es-HN", { minimumFractionDigits: 2 })}
                </p>
              </div>
            </div>

            {/* Final Account Balances */}
            <div className="rounded-lg border border-border p-3 space-y-2 bg-muted/20">
              <p className="text-xs font-semibold text-muted-foreground uppercase tracking-wider">
                Balances de Cuentas Calculados
              </p>
              <div className="space-y-1.5 divide-y divide-border/60">
                {Object.entries(result.summary.account_balances).map(([accName, balance]) => (
                  <div key={accName} className="flex items-center justify-between pt-1.5 first:pt-0 text-xs">
                    <span className="font-medium text-foreground">{accName}</span>
                    <span className="font-mono font-bold text-foreground">
                      L. {Number(balance).toLocaleString("es-HN", { minimumFractionDigits: 2 })}
                    </span>
                  </div>
                ))}
              </div>
            </div>
          </div>
        )}

        <DialogFooter className="flex flex-col-reverse sm:flex-row gap-2 pt-2">
          {!result ? (
            <>
              <Button
                type="button"
                variant="outline"
                onClick={() => handleOpenChange(false)}
                disabled={isLoading}
              >
                Cancelar
              </Button>
              <Button
                type="button"
                onClick={() => handleExecuteImport(dryRun)}
                disabled={!file || isLoading}
                className="gap-2"
              >
                {isLoading ? (
                  <>
                    <Loader2 className="size-4 animate-spin" />
                    Procesando transacciones...
                  </>
                ) : (
                  <>
                    {dryRun ? "Ejecutar Simulación" : "Importar Datos"}
                    <ArrowRight className="size-4" />
                  </>
                )}
              </Button>
            </>
          ) : (
            <>
              <Button
                type="button"
                variant="outline"
                onClick={resetState}
                className="gap-1.5"
              >
                <RotateCcw className="size-3.5" />
                Probar otro archivo
              </Button>
              {result.dry_run ? (
                <Button
                  type="button"
                  onClick={() => handleExecuteImport(false)}
                  disabled={isLoading}
                  className="gap-1.5 bg-emerald-600 hover:bg-emerald-700 text-white"
                >
                  {isLoading ? (
                    <Loader2 className="size-4 animate-spin" />
                  ) : (
                    <Check className="size-4" />
                  )}
                  Guardar Datos en Cartera
                </Button>
              ) : (
                <Button
                  type="button"
                  onClick={() => handleOpenChange(false)}
                  className="gap-1.5"
                >
                  <Check className="size-4" />
                  Listo, Cerrar
                </Button>
              )}
            </>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}
